// src/pages/Dashboard/sections/DashboardOverview.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getValidSignature, clearSignatureCache } from '../../../services/signatureService';
import '../styles/dashboardOverview.css';

/**
 * Dashboard Overview Section
 * Provides a summary of node status, network performance, and quick actions
 * Updated to use network stability fees terminology instead of commissions
 */
const DashboardOverview = ({ 
  wallet, 
  activatedNodes, 
  selectedNode, 
  networkStats, 
  formatNumber,
  formatDate,
  formatWalletAddress,
  refreshNodes
}) => {
  const [nodeActivity, setNodeActivity] = useState([]);
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [referralsError, setReferralsError] = useState('');
  
  /**
   * Convert network name/string to numeric ID
   * This is crucial for API compatibility
   * @param {string|number} networkId - Network name or ID
   * @returns {number} - Numeric network ID
   */
  const convertNetworkToNumeric = (networkId) => {
    if (typeof networkId === 'number') return networkId;
    
    // If it's already a numeric string, convert it
    if (!isNaN(parseInt(networkId))) return parseInt(networkId);
    
    // Map network names to IDs
    const networkMap = {
      'ethereum': 1,
      'eth': 1,
      'bsc': 2,
      'binance': 2,
      'solana': 3,
      'sol': 3,
      'polygon': 4
    };
    
    // Convert to lowercase to ensure matching
    const networkName = String(networkId).toLowerCase();
    return networkMap[networkName] || 1; // Default to 1 (ethereum) if not found
  };
  
  // Fetch node activity and referrals data when selected node changes
  useEffect(() => {
    if (selectedNode) {
      fetchNodeActivity();
      fetchNodeReferrals();
    }
  }, [selectedNode]);
  
  /**
   * Fetch node activity data from API
   * Updated to use the team-points endpoint for better compatibility
   */
  const fetchNodeActivity = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoadingActivity(true);
    setActivityError('');
    
    try {
      // Convert network ID to numeric format for API compatibility
      const networkId = convertNetworkToNumeric(wallet.networkId || wallet.network || 1);
      
      try {
        // Get a valid signature with potential caching
        const { signature, message } = await getValidSignature(
          wallet.address,
          networkId,
          wallet.type || 'metamask',
          false // Don't force refresh initially
        );
        
        // Fetch activity data from API
        // Note: Using team-points endpoint to get recent activities as per API documentation
        const response = await fetch('https://api.aeronyx.network/api/nodes/team-points/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet_address: wallet.address,
            blockchain_network_id: networkId, // Use numeric network ID
            signature: signature,
            message: message,
            wallet_type: wallet.type,
            node_id: selectedNode.id,
            period: '30d'  // Last 30 days of activity
          }),
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
          // Check if it's an authentication error
          if (result.message && (
            result.message.toLowerCase().includes('signature') || 
            result.message.toLowerCase().includes('auth') ||
            result.message.toLowerCase().includes('token'))
          ) {
            // Clear signature cache and retry
            clearSignatureCache();
            throw new Error('Signature validation failed, retrying...');
          }
          throw new Error(result.message || 'Failed to fetch node activity');
        }
        
        // Extract recent distributions and transform to activity format
        const distributions = result.data?.recent_distributions || [];
        const transformedActivity = distributions.map(dist => ({
          type: 'network_stability',
          timestamp: new Date(dist.created_at),
          value: dist.amount,
          description: `Network stability fee from ${formatWalletAddress(dist.source_wallet || '')}`
        }));
        
        // Add other activity types if available in the API response
        // Limit to 5 most recent activities
        setNodeActivity(transformedActivity.slice(0, 5));
      } catch (signError) {
        console.warn('First attempt error in fetchNodeActivity:', signError);
        
        // If first attempt fails, try again with a forced new signature
        if (signError.message && signError.message.includes('retrying')) {
          try {
            // Get a fresh signature
            const { signature, message } = await getValidSignature(
              wallet.address,
              networkId,
              wallet.type || 'metamask',
              true // Force refresh signature
            );
            
            // Retry the API call with the new signature
            const response = await fetch('https://api.aeronyx.network/api/nodes/team-points/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                wallet_address: wallet.address,
                blockchain_network_id: networkId,
                signature: signature,
                message: message,
                wallet_type: wallet.type,
                node_id: selectedNode.id,
                period: '30d'
              }),
            });
            
            if (!response.ok) {
              throw new Error(`API error: ${response.status} ${response.statusText}`);
            }
            
            const result = await response.json();
            
            if (!result.success) {
              throw new Error(result.message || 'Failed to fetch node activity');
            }
            
            // Process the data as before
            const distributions = result.data?.recent_distributions || [];
            const transformedActivity = distributions.map(dist => ({
              type: 'network_stability',
              timestamp: new Date(dist.created_at),
              value: dist.amount,
              description: `Network stability fee from ${formatWalletAddress(dist.source_wallet || '')}`
            }));
            
            setNodeActivity(transformedActivity.slice(0, 5));
          } catch (retryError) {
            console.error('Retry failed in fetchNodeActivity:', retryError);
            throw retryError;
          }
        } else {
          // If it's not a signature error, rethrow
          throw signError;
        }
      }
    } catch (err) {
      console.error('Error fetching node activity:', err);
      setActivityError('Failed to load activity data');
      
      // Generate placeholder data for demo/development
      generatePlaceholderActivity();
    } finally {
      setIsLoadingActivity(false);
    }
  };
  
  /**
   * Fetch node referrals data from API
   * Updated to use the nodes/referrals endpoint as per API documentation
   */
  const fetchNodeReferrals = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoadingReferrals(true);
    setReferralsError('');
    
    try {
      // Convert network ID to numeric format for API compatibility
      const networkId = convertNetworkToNumeric(wallet.networkId || wallet.network || 1);
      
      try {
        // Get a valid signature with potential caching
        const { signature, message } = await getValidSignature(
          wallet.address,
          networkId,
          wallet.type || 'metamask',
          false // Don't force refresh initially
        );
        
        // Fetch referrals data from API using the nodes/referrals endpoint as per API doc
        const response = await fetch('https://api.aeronyx.network/api/nodes/referrals/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet_address: wallet.address,
            blockchain_network_id: networkId, // Use numeric network ID
            signature: signature,
            message: message,
            wallet_type: wallet.type,
            node_id: selectedNode.id,
            page: 1,
            page_size: 5  // Limit to 5 most recent referrals
          }),
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
          // Check if it's an authentication error
          if (result.message && (
            result.message.toLowerCase().includes('signature') || 
            result.message.toLowerCase().includes('auth') ||
            result.message.toLowerCase().includes('token'))
          ) {
            // Clear signature cache and retry
            clearSignatureCache();
            throw new Error('Signature validation failed, retrying...');
          }
          throw new Error(result.message || 'Failed to fetch referrals');
        }
        
        // Transform referrals data to the format needed by the UI
        const referrals = result.data?.referrals || [];
        const transformedReferrals = referrals.map(ref => ({
          id: ref.id,
          code: ref.reference_code || 'N/A',
          date: new Date(ref.created_at),
          status: ref.status || 'pending',
          wallet: formatWalletAddress(ref.wallet_address || '')
        }));
        
        setRecentReferrals(transformedReferrals.slice(0, 5));
      } catch (signError) {
        console.warn('First attempt error in fetchNodeReferrals:', signError);
        
        // If first attempt fails, try again with a forced new signature
        if (signError.message && signError.message.includes('retrying')) {
          try {
            // Get a fresh signature
            const { signature, message } = await getValidSignature(
              wallet.address,
              networkId,
              wallet.type || 'metamask',
              true // Force refresh signature
            );
            
            // Retry the API call with the new signature
            const response = await fetch('https://api.aeronyx.network/api/nodes/referrals/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                wallet_address: wallet.address,
                blockchain_network_id: networkId,
                signature: signature,
                message: message,
                wallet_type: wallet.type,
                node_id: selectedNode.id,
                page: 1,
                page_size: 5
              }),
            });
            
            if (!response.ok) {
              throw new Error(`API error: ${response.status} ${response.statusText}`);
            }
            
            const result = await response.json();
            
            if (!result.success) {
              throw new Error(result.message || 'Failed to fetch referrals');
            }
            
            // Process the data as before
            const referrals = result.data?.referrals || [];
            const transformedReferrals = referrals.map(ref => ({
              id: ref.id,
              code: ref.reference_code || 'N/A',
              date: new Date(ref.created_at),
              status: ref.status || 'pending',
              wallet: formatWalletAddress(ref.wallet_address || '')
            }));
            
            setRecentReferrals(transformedReferrals.slice(0, 5));
          } catch (retryError) {
            console.error('Retry failed in fetchNodeReferrals:', retryError);
            throw retryError;
          }
        } else {
          // If it's not a signature error, rethrow
          throw signError;
        }
      }
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setReferralsError('Failed to load referrals data');
      
      // Generate placeholder data for demo/development
      generatePlaceholderReferrals();
    } finally {
      setIsLoadingReferrals(false);
    }
  };
  
  // Generate placeholder activity data for development/demo purposes
  const generatePlaceholderActivity = () => {
    const activities = [
      { 
        type: 'network_stability', 
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), 
        value: 245.32, 
        description: 'Network stability fee from downline RWA'
      },
      { 
        type: 'referral', 
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000), 
        value: null, 
        description: 'New referral joined your team'
      },
      { 
        type: 'points', 
        timestamp: new Date(Date.now() - 72 * 60 * 60 * 1000), 
        value: 1240, 
        description: 'Points generated from network activity'
      },
      { 
        type: 'system', 
        timestamp: new Date(Date.now() - 96 * 60 * 60 * 1000), 
        value: null, 
        description: 'Node status changed to Active'
      }
    ];
    
    setNodeActivity(activities);
  };
  
  // Generate placeholder referrals data for development/demo purposes
  const generatePlaceholderReferrals = () => {
    const referrals = [
      {
        id: 1,
        code: 'ALD-95423',
        date: new Date(Date.now() - 36 * 60 * 60 * 1000),
        status: 'active',
        wallet: '0x3ab4...f128'
      },
      {
        id: 2,
        code: 'ALD-84591',
        date: new Date(Date.now() - 72 * 60 * 60 * 1000),
        status: 'pending',
        wallet: '0x6fc1...a3e7'
      },
      {
        id: 3,
        code: 'ALD-77632',
        date: new Date(Date.now() - 120 * 60 * 60 * 1000),
        status: 'active',
        wallet: '0x2de8...9b45'
      }
    ];
    
    setRecentReferrals(referrals);
  };
  
  // Format date relative to now (e.g., "2 days ago")
  const formatRelativeDate = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  };
  
  if (!wallet.connected) {
    return (
      <div className="section-placeholder">
        <p>Please connect your wallet to view your dashboard.</p>
      </div>
    );
  }
  
  if (activatedNodes.length === 0) {
    return (
      <div className="no-nodes-message">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="info-icon">
          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <h3>No Activated Nodes</h3>
        <p>You don't have any activated nodes yet. Apply for a node to start managing your network.</p>
        <a href="/node-application" className="btn btn-primary">Apply for a Node</a>
      </div>
    );
  }
  
  // Get counts by tier
  const getNodeCountsByTier = () => {
    const counts = {
      apex: 0,
      master: 0,
      super: 0,
      standard: 0,
      total: activatedNodes.length
    };
    
    activatedNodes.forEach(node => {
      const tierName = (node.tier_name || node.tier || '').toLowerCase();
      if (tierName.includes('apex')) counts.apex++;
      else if (tierName.includes('master')) counts.master++;
      else if (tierName.includes('super')) counts.super++;
      else counts.standard++;
    });
    
    return counts;
  };
  
  const nodeCounts = getNodeCountsByTier();
  
  // Format network stability fees (replacing commissions)
  // Note terminology change from commissions to network_stability_fees as per requirements
  const formattedStabilityFees = networkStats?.network_stability_fees 
    ? formatNumber(networkStats.network_stability_fees, 2)
    : (networkStats?.total_commissions  // Backward compatibility
      ? formatNumber(networkStats.total_commissions, 2)
      : '0');
  
  return (
    <div className="dashboard-overview-section">
      <div className="overview-header">
        <h2>Node Team Dashboard</h2>
        <p className="overview-subtitle">
          Welcome to your node team dashboard! Here's your network's current status.
        </p>
      </div>
      
      <div className="overview-grid">
        {/* Nodes Summary Card */}
        <div className="overview-card nodes-summary">
          <h3>Your Nodes</h3>
          <div className="nodes-count-grid">
            <div className="node-type-count">
              <div className="count">{nodeCounts.total}</div>
              <div className="label">Total Nodes</div>
            </div>
            {nodeCounts.apex > 0 && (
              <div className="node-type-count apex">
                <div className="count">{nodeCounts.apex}</div>
                <div className="label">Apex</div>
              </div>
            )}
            {nodeCounts.master > 0 && (
              <div className="node-type-count master">
                <div className="count">{nodeCounts.master}</div>
                <div className="label">Master</div>
              </div>
            )}
            {nodeCounts.super > 0 && (
              <div className="node-type-count super">
                <div className="count">{nodeCounts.super}</div>
                <div className="label">Super</div>
              </div>
            )}
            {nodeCounts.standard > 0 && (
              <div className="node-type-count standard">
                <div className="count">{nodeCounts.standard}</div>
                <div className="label">Standard</div>
              </div>
            )}
          </div>
          <div className="card-actions">
            <Link to="/dashboard/nodes" className="action-link">
              <span>Manage Nodes</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Network Stats Card */}
        <div className="overview-card network-stats">
          <h3>Network Statistics</h3>
          <div className="network-stats-grid">
            <div className="network-stat">
              <div className="stat-value">
                {networkStats?.direct_subordinates || 0}
              </div>
              <div className="stat-label">
                Direct Subordinates
              </div>
            </div>
            <div className="network-stat">
              <div className="stat-value">
                {networkStats?.total_network_size || 0}
              </div>
              <div className="stat-label">
                Total Network Size
              </div>
            </div>
            <div className="network-stat highlight">
              <div className="stat-value">
                {formattedStabilityFees}
              </div>
              <div className="stat-label">
                Network Stability Fees
              </div>
            </div>
          </div>
          <div className="card-actions">
            <Link to="/dashboard/network" className="action-link">
              <span>View Network</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Quick Actions Card */}
        <div className="overview-card quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <Link to="/dashboard/invitations" className="quick-action">
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="action-name">Generate Invitation</div>
            </Link>
            
            <Link to="/dashboard/network" className="quick-action">
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="action-name">View Team Network</div>
            </Link>
            
            <Link to="/dashboard/nodes" className="quick-action">
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C17.7699 3.58317 19.0078 5.17799 19.0078 7.005C19.0078 8.83201 17.7699 10.4268 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="action-name">Bind to Superior</div>
            </Link>
            
            <a href="/portfolio" className="quick-action">
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="action-name">My RWA Portfolio</div>
            </a>
          </div>
        </div>
        
        {/* Activity Feed Card */}
        <div className="overview-card activity-feed">
          <h3>Recent Activity</h3>
          {isLoadingActivity ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading activity data...</p>
            </div>
          ) : activityError ? (
            <div className="error-message">
              <p>{activityError}</p>
            </div>
          ) : nodeActivity.length > 0 ? (
            <div className="activity-list">
              {nodeActivity.map((activity, index) => (
                <div className="activity-item" key={index}>
                  <div className={`activity-icon ${activity.type}`}>
                    {activity.type === 'network_stability' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {activity.type === 'referral' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M20 8v6M23 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {activity.type === 'points' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {activity.type === 'system' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="activity-content">
                    <div className="activity-description">{activity.description}</div>
                    <div className="activity-meta">
                      <span className="activity-time">{formatRelativeDate(activity.timestamp)}</span>
                      {activity.value !== null && (
                        <span className="activity-value">
                          {formatNumber(activity.value, 2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-activity">
              <p>No recent activity to display</p>
            </div>
          )}
        </div>
        
        {/* Recent Referrals Card */}
        <div className="overview-card recent-referrals">
          <h3>Recent Referrals</h3>
          {isLoadingReferrals ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading referrals data...</p>
            </div>
          ) : referralsError ? (
            <div className="error-message">
              <p>{referralsError}</p>
            </div>
          ) : recentReferrals.length > 0 ? (
            <div className="referrals-table-container">
              <table className="referrals-table">
                <thead>
                  <tr>
                    <th>Node</th>
                    <th>Status</th>
                    <th>When</th>
                    <th>Wallet</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReferrals.map((referral) => (
                    <tr key={referral.id}>
                      <td className="node-col">{referral.code}</td>
                      <td>
                        <span className={`status-badge ${referral.status}`}>
                          {referral.status}
                        </span>
                      </td>
                      <td>{formatRelativeDate(referral.date)}</td>
                      <td className="wallet-col">{referral.wallet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-referrals">
              <p>No recent referrals to display</p>
            </div>
          )}
          <div className="card-actions">
            <Link to="/dashboard/invitations" className="action-link">
              <span>View All Referrals</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Performance Summary Card */}
        <div className="overview-card performance-summary">
          <h3>Performance Summary</h3>
          <div className="performance-metrics">
            <div className="performance-metric">
              <div className="metric-label">Network Growth Rate</div>
              <div className="metric-bar">
                <div className="metric-progress" style={{ width: '65%' }}></div>
              </div>
              <div className="metric-value">65%</div>
            </div>
            
            <div className="performance-metric">
              <div className="metric-label">Points Generation</div>
              <div className="metric-bar">
                <div className="metric-progress" style={{ width: '82%' }}></div>
              </div>
              <div className="metric-value">82%</div>
            </div>
            
            <div className="performance-metric">
              <div className="metric-label">Stability Fee Rate</div>
              <div className="metric-bar">
                <div className="metric-progress" style={{ width: '73%' }}></div>
              </div>
              <div className="metric-value">73%</div>
            </div>
            
            <div className="performance-metric">
              <div className="metric-label">Asset Conversion</div>
              <div className="metric-bar">
                <div className="metric-progress" style={{ width: '48%' }}></div>
              </div>
              <div className="metric-value">48%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
